import { IRootScopeService, ITimeoutService, IScope, IPromise, IQService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IAssigneeIdMap, IAssignee } from "@gtmhub/assignees/models";
import { IGoal } from "@gtmhub/goals/models";
import {
  IAutomationTargetProperty,
  IAutomation,
  IAutomationCondition,
  IAutomationTask,
  IAutomationTargetPropertiesParams,
  IAutomationRuleBounds,
  IPropertyNames,
  ICustomFieldProperties,
  ITargetPropertiesTypes,
  ISelectedProperty,
  PropertiesDictionary,
  PropertiesDictionaryKeys,
  AutomationTriggerType,
  AutomationOperationByTargetType,
  AutomationConditionBooleanOperator,
  AutomationRuleOperator,
} from "../models/automation";
import { ICustomField } from "@gtmhub/customFields/models/models";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AutomationService } from "../services/automation";
import { GoalService } from "@gtmhub/goals/goal.service";
import { localize } from "@gtmhub/localization";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { INgRedux } from "@gtmhub/state-management";
import { IStateInit } from "@gtmhub/core/routing";
import { AssigneeActions } from "../../assignees/redux/assignee-actions";
import { GoalModalsService } from "../../goals/goalModalsService";
import { IMetric } from "../../okrs/metrics/models/models";
import { manuallyMultiplyBy100, enrichRuleOperators, enrichAggregator, isPredefinedRuleValue, isPredefinedDateValue } from "../utils/utils";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { GoalsChangedEventArgs, goalSelected, OkrEvents } from "@gtmhub/okrs";
import dayjs from "@gtmhub/dayjs";

export type PredefinedDate = {
  display: "1 day" | "2 days" | "3 days" | "1 week" | "2 weeks" | "3 weeks" | "custom timeframe" | "custom date";
  value: "1 day" | "2 days" | "3 days" | "1 week" | "2 weeks" | "3 weeks" | "custom timeframe" | "custom date";
  type: "day" | "days" | "week" | "weeks" | "custom";
};

const isCompareCombo = (condition: IAutomationCondition, rule: AutomationRuleOperator): boolean => {
  if (rule === "compareTo" || rule === "previousValue") {
    return true;
  }
};

interface IAutomationConditionUI extends IAutomationCondition {
  showUserSelector?: boolean;
}
export interface IAutomationConditionScope extends IScope {
  assigneesIdMap: IAssigneeIdMap;
  conditionRules: IAutomationConditionUI[];
  assignees: IAssignee[];
  goal: IGoal;
  properties: ISelectedProperty[];
  selectionStrings: { [key: string]: string };
  selectedItem: {
    objectType: string;
    operation: AutomationOperationByTargetType[AutomationTriggerType];
    manual?: boolean;
  };
  showCalendar: boolean;
  indicators: {
    saving: boolean;
    loading: boolean;
  };
  propertiesDictionary: PropertiesDictionary;
  ruleDictionary;
  customFields: ICustomField[];
  editMode: boolean;
  sessions: IPlanningSession[];
  datePredefinedValues: PredefinedDate[];

  addCondition(): void;
  defineCondition(): void;
  removeCondition(index: number): void;
  setPredefinedValue(condition: IAutomationCondition, date: PredefinedDate): void;
  setCustomPredefinedValue(condition: IAutomationCondition, value: number): void;
  setConditionRule(condition: IAutomationCondition, rule: AutomationRuleOperator): void;
  setConditionComboRule(condition: IAutomationCondition, rule: AutomationRuleOperator): void;
  filterRuleOperators(params?: { onlyDifferenceOperators: boolean }): (operator: string) => string;
  isOperatorDisabled(operator: string): boolean;
  getRuleBeautyName(condition: IAutomationCondition): string;
  getPropertyBeautyName(property: string): PropertiesDictionaryKeys;
  changeBooleanOperator(operator: string): void;
  selectCustomFieldOption(option: { value: string }, condition: IAutomationCondition): void;
  selectProperty(property: unknown, index: number): void;
  getCustomInputDayValue(value: number): string;
  getAssignees(value: string): void;
  selectNewAssignee(assignee: IAssignee, index: number): void;
  selectMultipleAssignees(ids: string[], index: number): void;
  selectOkr(unselectableId: string, conditionIdx: string): void;
  removeSelectedOkr(conditionIdx: number): void;
  typeaheadBlur(index: number): void;
  setValueForSessionId(selectedProperty: ISelectedProperty, value: string): void;
}

interface IAutomationConditionCtrlStateParamsService {
  automationId?: string;
  automationTaskId?: string;
}

const bindStateToScope = (state: IAssigneesStoreState & ISessionsStoreState) => {
  const { items } = state.sessions;

  return {
    sessions: items,
    assignees: Object.values(state.assignees.map),
  };
};

const bindAssigneeIdMapToScope = (state: IAssigneesStoreState) => ({
  assigneesIdMap: state.assignees.map,
});

export class AutomationConditionCtrl implements IStateInit {
  private differenceOperators: AutomationRuleOperator[] = ["differenceEquals", "differenceGreater", "differenceNotEqual", "differenceLess"];
  private differenceOperator = "difference";
  private compareOperator: AutomationRuleOperator = "compareTo";
  private previousValueOperator: AutomationRuleOperator = "previousValue";
  private conditionIdx = "";
  private propertyNames: IPropertyNames;
  private customFieldProperties: ICustomFieldProperties;

  private datePredefinedValues: PredefinedDate[] = [
    { display: "1 day", value: "1 day", type: "day" },
    { display: "2 days", value: "2 days", type: "days" },
    { display: "3 days", value: "3 days", type: "days" },

    { display: "1 week", value: "1 week", type: "week" },
    { display: "2 weeks", value: "2 weeks", type: "weeks" },
    { display: "3 weeks", value: "3 weeks", type: "weeks" },

    { display: "custom timeframe", value: "custom timeframe", type: "custom" },
    { display: "custom date", value: "custom date", type: "custom" },
  ];

  private currentAutomation: IAutomation<AutomationTriggerType> = null;

  private automationCustomFields = null;

  private booleanOperator: AutomationConditionBooleanOperator = "And";

  private automationStates: { rules: string } = {
    rules: "gtmhub.automation.rules",
  };

  private conditionDataTypes = {
    alphabetic: "alphabetic",
    date: "date",
    boolean: "boolean",
  };

  private okrEvents: OkrEvents;

  public static $inject = [
    "$q",
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$timeout",
    "AccountResolverService",
    "AutomationService",
    "GoalService",
    "UIErrorHandlingService",
    "$ngRedux",
    "PlanningSessionsActions",
    "AssigneeActions",
    "GoalModalsService",
    "FeatureToggleService",
  ];

  constructor(
    private $q: IQService,
    private $rootScope: IRootScopeService,
    private $scope: IAutomationConditionScope,
    private $state: StateService,
    private $stateParams: IAutomationConditionCtrlStateParamsService,
    private $timeout: ITimeoutService,
    private accountResolverService: AccountResolverService,
    private automationService: AutomationService,
    private goalService: GoalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions,
    private goalModalsService: GoalModalsService,
    private featureToggleService: FeatureToggleService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.indicators = {
      saving: false,
      loading: true,
    };

    this.$scope.editMode = !!this.$stateParams.automationTaskId;

    this.$scope.selectionStrings = {
      goal: localize("objective_cap"),
      metric: localize("key_result_cap"),
      user: localize("user_cap"),
      created: localize("is_created"),
      modified: localize("is_edited"),
      deleted: localize("is_deleted"),
      "progress changed": localize("progress_is_updated"),
      empty: localize("empty"),
    };
    this.$scope.conditionRules = [];
    this.$scope.showCalendar = false;
    this.$scope.datePredefinedValues = this.datePredefinedValues.filter((d) => d.type !== "custom");
    this.$scope.propertiesDictionary = this.automationService.getPropertiesDictionary();
    this.$scope.ruleDictionary = this.automationService.getRuleDictionary();
    this.propertyNames = this.automationService.getConditionPropertyNames();
    this.customFieldProperties = this.automationService.getCustomFieldProperties();

    this.$scope.addCondition = this.addCondition;
    this.$scope.defineCondition = this.defineCondition;
    this.$scope.removeCondition = this.removeCondition;
    this.$scope.setPredefinedValue = this.setPredefinedValue;
    this.$scope.setCustomPredefinedValue = this.setCustomPredefinedValue;
    this.$scope.setConditionRule = this.setConditionRule;
    this.$scope.setConditionComboRule = this.setConditionComboRule;
    this.$scope.filterRuleOperators = this.filterRuleOperators;
    this.$scope.isOperatorDisabled = this.isOperatorDisabled;
    this.$scope.getRuleBeautyName = this.getRuleBeautyName;
    this.$scope.getPropertyBeautyName = this.getPropertyBeautyName;
    this.$scope.changeBooleanOperator = this.changeBooleanOperator;
    this.$scope.selectCustomFieldOption = this.selectCustomFieldOption;
    this.$scope.selectProperty = this.selectProperty;
    this.$scope.getCustomInputDayValue = this.getCustomInputDayValue;
    this.$scope.getAssignees = this.getAssignees;
    this.$scope.selectNewAssignee = this.selectNewAssignee;
    this.$scope.selectMultipleAssignees = this.selectMultipleAssignees;
    this.$scope.selectOkr = this.selectOkr;
    this.$scope.removeSelectedOkr = this.removeSelectedOkr;
    this.$scope.typeaheadBlur = this.typeaheadBlur;
    this.$scope.setValueForSessionId = this.setValueForSessionId;

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", () => {
      unsubscribe();
    });
  }

  public stateInit(): IPromise<unknown> {
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    return this.init();
  }

  private init = (): IPromise<unknown> => {
    return this.automationService.getAutomation(this.$stateParams.automationId).then(
      (automation) => {
        this.$scope.indicators.loading = false;

        this.currentAutomation = automation;
        this.$scope.selectedItem = this.currentAutomation.trigger;

        return this.getConditionPropertiesTypes(this.currentAutomation.trigger.objectType);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private getConditionPropertiesTypes = (targetType: AutomationTriggerType): IPromise<unknown> => {
    const targetQueryParams: IAutomationTargetPropertiesParams = {
      targetType,
    };

    return this.$q
      .all({
        targetProperties: this.automationService.getTargetPropertiesTypes(targetQueryParams),
        isTaskEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleTasks),
        isTagsEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleTags),
      })
      .then(
        ({ targetProperties, isTaskEnabled, isTagsEnabled }) => {
          this.processTargetProperties({ targetProperties, isTaskEnabled, isTagsEnabled });

          if (!this.$scope.editMode) {
            this.createCondition();
          } else {
            this.editCondition();
          }
        },
        (rejection) => this.uiErrorHandlingService.handleModal(rejection)
      );
  };

  private processTargetProperties(params: { targetProperties: ITargetPropertiesTypes; isTaskEnabled: boolean; isTagsEnabled: boolean }): void {
    const mergedProperties = params.targetProperties.properties.concat(params.targetProperties.customFieldsProperties || []);
    this.$scope.properties = enrichAggregator(this.enrichPropertiesWithCompareOperator, enrichRuleOperators)(mergedProperties);

    if (!params.isTaskEnabled) {
      this.$scope.properties = this.$scope.properties.filter((prop) => !prop.propertyName.toLowerCase().includes("taskscount"));
    }

    if (!params.isTagsEnabled) {
      this.$scope.properties = this.$scope.properties.filter((prop) => !prop.propertyName.toLowerCase().includes("tags"));
    }

    this.automationCustomFields = params.targetProperties.customFieldsProperties || [];
  }

  private createCondition(): void {
    const generatedConditionBase = this.generateConditionBase({ isFirstCondition: true });
    this.setDefaultConditionValue(generatedConditionBase);
    this.$scope.conditionRules.push(generatedConditionBase);
  }

  private addCondition = (): void => {
    const generatedConditionBase = this.generateConditionBase();
    this.setDefaultConditionValue(generatedConditionBase);
    this.$scope.conditionRules.push(generatedConditionBase);

    if (this.$scope.conditionRules.length === 1) {
      // if it is the first element it becomes the one determing the booleanOperator
      this.$scope.conditionRules[0].firstCondition = true;
    }
  };

  private editCondition(): void {
    const currentAutomationTask = this.getAutomationTask(this.currentAutomation, this.$stateParams.automationTaskId, (automationTask) => automationTask);

    this.booleanOperator = currentAutomationTask.rule.booleanLogic;
    currentAutomationTask.rule.ruleBounds.forEach((condition, index) => {
      const selectedProperty = this.getSelectedPropertiesBasedOnCondition(condition);

      // if missing the condition has been a customfield that is now deleted
      if (!selectedProperty) {
        return;
      }

      selectedProperty.input = condition.value;

      const conditionRule: IAutomationCondition = {
        booleanOperator: currentAutomationTask.rule.booleanLogic,
        selectedProperty: selectedProperty,
        selectedOperator: condition.operator,
        firstCondition: index === 0,
      };

      this.processConditionBasedOnRule(condition, conditionRule, index, selectedProperty);

      // filling assignees id map if missing and only if editMode is on and the condition is one of the below ones
      if (
        condition.fieldName === this.propertyNames.createdById ||
        condition.fieldName === this.propertyNames.modifiedById ||
        (condition.fieldName === this.propertyNames.ownerId && !this.$scope.assigneesIdMap)
      ) {
        const unsubscribe = this.$ngRedux.connect(bindAssigneeIdMapToScope)(this.$scope);
        this.$scope.$on("$destroy", unsubscribe);
        this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
      }
    });
  }

  private defineCondition = (): void => {
    if (this.$scope.editMode) {
      this.editAutomationTask();
      return;
    }

    const conditionTask: IAutomationTask = {
      accountId: this.accountResolverService.getAccountId(),
      automationId: this.$stateParams.automationId,
      rule: {
        ruleBounds: [],
        booleanLogic: this.booleanOperator,
      },
    };

    this.$scope.conditionRules.forEach((rule) => {
      let ruleValue: number = rule.selectedProperty.input;
      if (rule.selectedProperty.propertyName === this.propertyNames.attainment) {
        ruleValue = rule.selectedProperty.input / 100;
      }

      const isCustomFieldRule: { isCustom: boolean } = this.automationCustomFields.reduce(
        (isCustomRule: { isCustom: boolean }, el) => {
          return el.propertyName === rule.selectedProperty.propertyName ? (isCustomRule = { isCustom: true }) : isCustomRule;
        },
        { isCustom: false }
      );

      const newRule: IAutomationRuleBounds = {
        fieldName: rule.selectedProperty.propertyName,
        operator: rule.selectedCompareOperator || rule.selectedOperator,
        value: ruleValue,
        useOldValue: rule.useOldValue,
        dataType: rule.selectedProperty.dataType,
      };

      if (newRule.operator === "inLast") {
        newRule.operator = "greater";
      }

      if (newRule.operator === "notInLast") {
        newRule.operator = "less";
      }

      if (isCustomFieldRule.isCustom) {
        newRule.customField = true;
        newRule.dataType = rule.selectedProperty.dataType;
      }

      conditionTask.rule.ruleBounds.push(newRule);
    });

    // if no automationTask is present means we are creating new condition
    // else we are editing existing automationTask
    this.createNextConditionRecursive(this.currentAutomation, conditionTask, this.currentAutomation && !this.currentAutomation.automationTask);
  };

  private removeCondition = (index: number): void => {
    if (index === 0 && this.$scope.conditionRules.length > 1) {
      // if we remove the first element then the next should become the one determining the booleanOperator
      this.$scope.conditionRules[1].firstCondition = true;
    }
    this.$scope.conditionRules.splice(index, 1);
  };

  private generateConditionBase = (params: { isFirstCondition: boolean } = { isFirstCondition: false }): IAutomationCondition => {
    return {
      booleanOperator: this.booleanOperator,
      selectedProperty: angular.copy(this.$scope.properties[2]),
      selectedOperator: angular.copy(this.$scope.properties[2].ruleOperators[0]),
      firstCondition: params.isFirstCondition,
    };
  };

  private getSelectedPropertiesBasedOnCondition(condition: IAutomationRuleBounds): ISelectedProperty {
    let selectedProperty: ISelectedProperty;
    this.$scope.properties.forEach((property) => {
      if (property.propertyName === condition.fieldName) {
        selectedProperty = angular.copy(property);
      }
    });
    return selectedProperty;
  }

  private processConditionBasedOnRule(condition: IAutomationRuleBounds, conditionRule: IAutomationCondition, index: number, selectedProperty: ISelectedProperty) {
    if (condition.useOldValue && condition.operator !== "greater" && condition.operator !== "less" && !condition.operator.includes(this.differenceOperator)) {
      this.setConditionRule(conditionRule, this.previousValueOperator);
      this.$scope.setConditionComboRule(conditionRule, condition.operator);
    }

    if (condition.useOldValue && condition.operator.includes(this.differenceOperator)) {
      this.setConditionRule(conditionRule, this.compareOperator);
      this.$scope.setConditionComboRule(conditionRule, condition.operator);
    }

    if (condition.dataType === "date") {
      if (isPredefinedRuleValue(condition) && condition.operator === "greater") {
        this.setConditionRule(conditionRule, "inLast");
        this.setPredefiendDay(conditionRule);
      } else if (isPredefinedRuleValue(condition) && condition.operator === "less") {
        this.setConditionRule(conditionRule, "notInLast");
        this.setPredefiendDay(conditionRule);
      } else {
        conditionRule.showDatePicker = true;
      }
    }

    this.$scope.conditionRules.push(conditionRule);

    if (condition.fieldName === this.propertyNames.session || condition.fieldName === this.propertyNames.goalSessionId) {
      this.setConditionSession(index, this.$scope.sessions);
    }

    if (condition.fieldName === this.propertyNames.attainment) {
      this.$scope.conditionRules[index].selectedProperty.input = manuallyMultiplyBy100(this.$scope.conditionRules[index].selectedProperty.input);
    }

    this.setGoal(condition, selectedProperty, index);
  }

  private setDefaultConditionValue = (condition: IAutomationCondition): void => {
    condition.showDatePicker = false;
    condition.showDateNumericInput = false;

    // setting default condition values for newly added conditions
    switch (condition.selectedProperty.dataType) {
      case this.customFieldProperties.dropdown:
        {
          const predefinedValue = condition.selectedProperty.predefinedValues[0];
          condition.selectedProperty.input = typeof predefinedValue === "object" && predefinedValue.value ? predefinedValue.value : predefinedValue;
        }
        break;
      case this.customFieldProperties.multiselect:
        condition.selectedProperty.input = [];
        break;
      case this.conditionDataTypes.boolean:
        condition.selectedProperty.input = true;
        break;
      case this.conditionDataTypes.date:
        if (condition.selectedOperator !== "inLast" && condition.selectedOperator !== "notInLast") {
          const today = dayjs.utc().toISOString();
          condition.selectedProperty.input = today;
        }
        condition.showDatePicker = true;
        break;
    }

    condition.useOldValue = false;

    condition.compareComboOperators = [];
    condition.selectedCompareOperator = null;

    condition.selectedOperator = condition.selectedProperty.ruleOperators[0];
  };

  private setPredefinedValue = (condition: IAutomationCondition, date: PredefinedDate): void => {
    condition.showDateNumericInput = false;
    condition.showDatePicker = false;
    condition.selectedProperty.inputValue = null;

    if (date.value === "custom timeframe") {
      condition.showDateNumericInput = true;

      if (condition.selectedProperty.input) {
        condition.selectedProperty.inputValue = +condition.selectedProperty.input.split(" ")[0];
      } else {
        condition.selectedProperty.inputValue = 1; // default
      }

      condition.selectedProperty.inputName = date.display;
      return;
    }

    if (date.value === "custom date") {
      condition.showDatePicker = true;
      condition.selectedProperty.inputName = date.display;
      return;
    }

    condition.selectedProperty.input = date.value;
    condition.selectedProperty.inputName = date.display;
  };

  private setCustomPredefinedValue = (condition: IAutomationCondition, value: number): void => {
    condition.selectedProperty.input = this.getCustomInputDayValue(value);
  };

  private setPredefiendDay = (conditionRule: IAutomationCondition): void => {
    const predefinedDays = this.datePredefinedValues.filter((pre) => pre.type === "day" || pre.type === "days" || pre.type === "week" || pre.type === "weeks");
    const isPredefinedDay = predefinedDays.some((pre) => pre.value === conditionRule.selectedProperty.input);

    if (!isPredefinedDay) {
      const isCustomDay = conditionRule.selectedProperty.input.includes("days");

      if (isCustomDay) {
        const customTimeFrame = this.datePredefinedValues.find((p) => p.value === "custom timeframe");
        this.$scope.setPredefinedValue(conditionRule, customTimeFrame);
      } else {
        const customDate = this.datePredefinedValues.find((p) => p.value === "custom date");
        this.$scope.setPredefinedValue(conditionRule, customDate);
      }
    }
  };

  private setConditionSession = (index: number, sessions: IPlanningSession[]): void => {
    if (Object.keys(this.$scope.conditionRules[index].selectedProperty).indexOf("input") === -1) {
      this.$scope.conditionRules[index].selectedProperty.input = sessions[0].id;
    }

    if (!this.$scope.conditionRules[index].selectedProperty.input) {
      this.$scope.conditionRules[index].selectedProperty.input = "";
    }
  };

  private setConditionRule = (condition: IAutomationCondition, rule: AutomationRuleOperator): void => {
    const oldRuleOperator: AutomationRuleOperator = condition.selectedOperator;

    condition.selectedOperator = rule;
    condition.compareComboOperators = []; // resetting
    condition.useOldValue = false; // resetting
    condition.selectedCompareOperator = null;

    // changing condition rule should cleanup predefined combos
    condition.showDateNumericInput = false;
    condition.showDatePicker = false;

    // if compareTo is chosen, we setup a combo strike for multiple operators
    if (isCompareCombo(condition, rule)) {
      condition.useOldValue = true;

      condition.compareComboOperators = [
        ...condition.selectedProperty.ruleOperators.filter(
          (operator) =>
            this.filterRuleOperator({ operator, onlyDifferenceOperators: true }) && operator !== this.compareOperator && operator !== this.previousValueOperator
        ),
      ];

      if (rule === this.previousValueOperator) {
        condition.compareComboOperators = [
          ...condition.selectedProperty.ruleOperators.filter(
            (operator) =>
              this.filterRuleOperator({ operator, onlyDifferenceOperators: false }) && operator !== this.compareOperator && operator !== this.previousValueOperator
          ),
        ];
      }

      condition.selectedCompareOperator = condition.compareComboOperators[0]; // by default choosing the first one in array
    }

    // using date -> & operator == older show predefined values
    this.setConditionRuleDate({ condition, rule, oldRuleOperator });
  };

  private setConditionRuleDate = (params: { condition: IAutomationCondition; rule: AutomationRuleOperator; oldRuleOperator: AutomationRuleOperator }): void => {
    if (params.condition.selectedProperty.dataType === "date") {
      const operatorList: AutomationRuleOperator[] = ["inLast", "notInLast"];
      const isInputPredefinedValue = isPredefinedDateValue(params.condition.selectedProperty.input);

      if (!operatorList.includes(params.rule)) {
        if (isInputPredefinedValue) {
          const today = dayjs.utc().toISOString();
          params.condition.selectedProperty.input = today;
        }
        params.condition.showDatePicker = true;
      } else {
        if (!isInputPredefinedValue) {
          params.condition.selectedProperty.input = this.datePredefinedValues[0].value;
        }
        params.condition.selectedProperty.input = params.condition.selectedProperty.input || this.datePredefinedValues[0].value;
      }
    }
  };

  private setConditionComboRule = (condition: IAutomationCondition, rule: AutomationRuleOperator): void => {
    if (condition.selectedOperator === this.compareOperator) {
      condition.compareComboOperators = this.differenceOperators;
    }

    condition.selectedCompareOperator = rule;
  };

  private filterRuleOperator = (params: { operator: AutomationRuleOperator; onlyDifferenceOperators: boolean }): string => {
    if (params.onlyDifferenceOperators && this.differenceOperators.indexOf(params.operator) !== -1) {
      return params.operator;
    }

    // non-difference operator filtration
    if (!params.onlyDifferenceOperators && this.differenceOperators.indexOf(params.operator) === -1) {
      return params.operator;
    }
  };

  private filterRuleOperators =
    (params: { onlyDifferenceOperators: boolean } = { onlyDifferenceOperators: false }) =>
    (operator: AutomationRuleOperator): string =>
      this.filterRuleOperator({ operator, onlyDifferenceOperators: params.onlyDifferenceOperators });

  private isOperatorDisabled = (operator: string): boolean => {
    return (operator === this.compareOperator || operator === this.previousValueOperator) && this.currentAutomation.trigger.operation !== "progress changed";
  };

  private getRuleBeautyName = (condition: IAutomationCondition): string => {
    return (
      this.$scope.ruleDictionary[condition.selectedProperty.dataType][condition.compareCombo] ||
      this.$scope.ruleDictionary[condition.selectedProperty.dataType][condition.selectedOperator]
    );
  };

  private getPropertyBeautyName = (property: string): PropertiesDictionaryKeys => {
    if (this.$scope.propertiesDictionary[property]) {
      return this.$scope.propertiesDictionary[property];
    }
    return property as PropertiesDictionaryKeys;
  };

  private enrichPropertiesWithCompareOperator = (properties: IAutomationTargetProperty[]): IAutomationTargetProperty[] => {
    return properties.map((prop) => {
      // only numeric fields have the compare operator so far
      // temp-fix: design score is currently disabled until a hotfix is applied on api
      if (prop.dataType === "numeric" && prop.propertyName !== "DesignScore") {
        prop.ruleOperators.push(this.compareOperator, this.previousValueOperator);
      }
      return prop;
    });
  };

  private changeBooleanOperator = (operator: AutomationConditionBooleanOperator): void => {
    this.booleanOperator = operator;
    // if we change And <=> Or all the condition changes their booleanOperator
    this.$scope.conditionRules = this.$scope.conditionRules.map((rule) => ({
      ...rule,
      booleanOperator: this.booleanOperator,
    }));
  };

  private selectCustomFieldOption = (option: { value: string }, condition: IAutomationCondition): void => {
    if (condition.selectedProperty.input.indexOf(option.value) === -1) {
      condition.selectedProperty.input.push(option.value);
      return;
    }

    condition.selectedProperty.input = condition.selectedProperty.input.filter((el) => el !== option.value);
  };

  private selectProperty = (property, index: number): void => {
    this.$scope.conditionRules[index].selectedProperty = angular.copy(property);

    this.setDefaultConditionValue(this.$scope.conditionRules[index]);

    if (property.fieldName === this.propertyNames.session || property.fieldName === this.propertyNames.goalSessionId) {
      this.setConditionSession(index, this.$scope.sessions);
    }

    if ([this.propertyNames.parent, this.propertyNames.objective].indexOf(property.propertyName) !== -1) {
      // default value
      this.$scope.conditionRules[index].selectedProperty.input = "";
      this.$scope.conditionRules[index].selectedProperty.inputName = this.$scope.selectionStrings.empty;
    }

    // filling assignees id map if missing and only the new property is one of the below ones
    if (
      property.propertyName === this.propertyNames.createdById ||
      property.propertyName === this.propertyNames.modifiedById ||
      (property.propertyName === this.propertyNames.ownerId && !this.$scope.assigneesIdMap)
    ) {
      const unsubscribe = this.$ngRedux.connect(bindAssigneeIdMapToScope)(this.$scope);
      this.$scope.$on("$destroy", unsubscribe);
      this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    }
  };

  private createNextConditionRecursive = (automation: IAutomation<AutomationTriggerType>, conditionTask: IAutomationTask, isRootTaskLevel?: boolean): void => {
    // if task is on root level
    if (isRootTaskLevel) {
      this.currentAutomation.automationTask = conditionTask;
      this.updateAutomation(this.currentAutomation);
      return;
    }

    // else find the hierarchy and put it on the found recursive level
    for (const property in automation) {
      // eslint-disable-next-line no-prototype-builtins
      if (automation.hasOwnProperty(property) && (property === "nextAutomationTask" || property === "automationTask")) {
        // if automationTask or nextAutomationTask is null or falsy means the hierarchy look up is done
        if (!automation[property]) {
          // setting the new condition in the automationTask
          automation[property] = conditionTask;

          // passing the full recursively built $scope.automation object in the format the backend api expects
          this.updateAutomation(this.currentAutomation);
        }

        // causing recursion until the top level hierarchy is found
        this.createNextConditionRecursive(automation[property], conditionTask);
      }
    }
  };

  private getCustomInputDayValue = (value: number): string => {
    if (value === 1) {
      return value + " " + localize("day");
    } else {
      return value + " " + localize("days");
    }
  };

  private getAssignees = (value: string): IAssignee[] => {
    return this.$scope.assignees.filter((assignee) => assignee.name.toLocaleLowerCase().includes(value.toLocaleLowerCase()));
  };

  private selectNewAssignee = (assignee: IAssignee, index: number): void => {
    this.$scope.conditionRules[index].selectedProperty.input = assignee.id;
    this.blur(index);
  };

  private selectMultipleAssignees = (ids: string[], index: number): void => {
    this.$scope.conditionRules[index].selectedProperty.input = ids;
  };

  private selectOkr = (unselectableId: string, conditionIdx: string): void => {
    this.goalModalsService.openGoalSelectModal({ invokedBy: "Automation", unselectableId, invokedById: unselectableId });
    this.conditionIdx = conditionIdx;
  };

  private removeSelectedOkr = (conditionIdx: number): void => {
    this.$scope.conditionRules[conditionIdx].selectedProperty.input = "";
    this.$scope.conditionRules[conditionIdx].selectedProperty.inputName = this.$scope.selectionStrings.empty;
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === goalSelected) {
      this.onOkrSelected(args.detail.goal);
    }
  };

  private onOkrSelected = (okr: IMetric | IGoal): void => {
    // first time selecting an okr
    if (!okr && !this.$scope.conditionRules[this.conditionIdx]) {
      return;
    }
    this.$scope.conditionRules[this.conditionIdx].selectedProperty.input = okr ? okr.id : "";
    this.$scope.conditionRules[this.conditionIdx].selectedProperty.inputName = okr ? okr.name : this.$scope.selectionStrings.empty;
    this.$scope.goal = <IGoal>{ ...okr };
  };

  private setGoal(condition: IAutomationRuleBounds, selectedProperty: ISelectedProperty, index: number): void {
    if ([this.propertyNames.parent, this.propertyNames.objective].indexOf(condition.fieldName) !== -1) {
      if (!this.$scope.goal) {
        if (selectedProperty.input) {
          this.goalService.getGoal(selectedProperty.input, { fields: "id,name" }).then(
            (goal) => {
              this.$scope.goal = goal;
              this.$scope.conditionRules[index].selectedProperty.input = goal.id;
              this.$scope.conditionRules[index].selectedProperty.inputName = goal.name;
            },
            (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true })
          );
        }
      } else {
        this.$scope.conditionRules[index].selectedProperty.input = this.$scope.goal.id;
        this.$scope.conditionRules[index].selectedProperty.inputName = this.$scope.goal.name;
      }
    }
  }

  private getAutomationTask = (
    automation: IAutomation<AutomationTriggerType>,
    automationTaskId: string,
    callback?: (automationTask: IAutomationTask) => IAutomationTask | unknown
  ): IAutomationTask => {
    return this.automationService.getAutomationTask(automation, automationTaskId, callback);
  };

  private editAutomationTask = (): void => {
    const ruleBounds = this.$scope.conditionRules.map((rule) => {
      let ruleValue: number = rule.selectedProperty.input;
      if (rule.selectedProperty.propertyName === this.propertyNames.attainment) {
        ruleValue = rule.selectedProperty.input / 100;
      }

      const isCustomFieldRule = this.automationCustomFields.reduce((isCustom: boolean, el) => {
        return el.propertyName === rule.selectedProperty.propertyName ? (isCustom = true) : isCustom;
      }, false);
      const newRule: IAutomationRuleBounds = {
        fieldName: rule.selectedProperty.propertyName,
        value: ruleValue,
        operator: rule.selectedCompareOperator || rule.selectedOperator,
        useOldValue: rule.useOldValue,
        dataType: rule.selectedProperty.dataType,
      };

      if (newRule.operator === "inLast") {
        newRule.operator = "greater";
      }

      if (newRule.operator === "notInLast") {
        newRule.operator = "less";
      }

      if (isCustomFieldRule) {
        newRule.customField = true;
        newRule.dataType = rule.selectedProperty.dataType;
      }

      return newRule;
    });

    this.getAutomationTask(this.currentAutomation, this.$stateParams.automationTaskId, (automationTask: IAutomationTask) => {
      automationTask.rule.booleanLogic = this.booleanOperator;
      automationTask.rule.ruleBounds = ruleBounds;
      this.updateAutomation(this.currentAutomation);
    });
  };

  private updateAutomation = (automation: IAutomation<AutomationTriggerType>): void => {
    this.$scope.indicators.saving = true;

    this.automationService.updateAutomation(automation).then(
      () => {
        this.$scope.indicators.saving = false;
        this.$rootScope.$broadcast("automationChanged");
        this.$state.go(this.automationStates.rules, { automationId: this.$stateParams.automationId }, { reload: false });
      },
      (error) => this.handleIndicatorError({ rejection: error, indicator: "saving", inline: true })
    );
  };

  private blur = (index: number): void => {
    this.$timeout(() => {
      this.$scope.conditionRules[index].showUserSelector = false;

      const ownerEl = document.querySelector<HTMLInputElement>(`#${this.$scope.conditionRules[index].selectedProperty.input} [name='owner']`);
      ownerEl.value = "";
      this.$timeout(() => ownerEl.blur());
    });
  };

  private typeaheadBlur = (index: number): void => {
    this.$timeout(() => {
      document.querySelector<HTMLInputElement>(`#${this.$scope.conditionRules[index].selectedProperty.input} [name='owner']`).value = "";
    });
  };

  private handleIndicatorError = <R>(params: { rejection: R; indicator: string; inline: boolean }): void => {
    this.uiErrorHandlingService.handleIndicator(params.rejection, { indicator: params.indicator, inline: params.inline });
  };

  private setValueForSessionId = (selectedProperty: ISelectedProperty, value: string): void => {
    selectedProperty.input = value;
  };
}
